/**
 * Scanner Error Boundary
 * 
 * Error boundary specifically designed for scanner components.
 * Shows appropriate fallback UI with camera permission and retry options.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, X, Camera } from 'lucide-react';
import { glassStyles, colors } from '@/lib/design';

interface ScannerErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
  onRetry?: () => void;
}

interface ScannerErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorType: 'camera' | 'scanner' | 'unknown';
}

/**
 * Detect if the error is camera-related
 */
function detectErrorType(error: Error): 'camera' | 'scanner' | 'unknown' {
  const message = error.message.toLowerCase();
  
  if (
    message.includes('permission') ||
    message.includes('notallowed') ||
    message.includes('camera') ||
    message.includes('getusermedia') ||
    message.includes('mediadevices')
  ) {
    return 'camera';
  }
  
  if (
    message.includes('dynamsoft') ||
    message.includes('cvr') ||
    message.includes('license') ||
    message.includes('capture vision')
  ) {
    return 'scanner';
  }
  
  return 'unknown';
}

export class ScannerErrorBoundary extends Component<ScannerErrorBoundaryProps, ScannerErrorBoundaryState> {
  public state: ScannerErrorBoundaryState = {
    hasError: false,
    error: null,
    errorType: 'unknown',
  };

  public static getDerivedStateFromError(error: Error): Partial<ScannerErrorBoundaryState> {
    return { 
      hasError: true, 
      error,
      errorType: detectErrorType(error),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error for debugging
    console.error('[ScannerErrorBoundary] Error caught:', error);
    console.error('[ScannerErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorType: 'unknown' });
    this.props.onRetry?.();
  };

  private handleClose = () => {
    this.props.onClose();
  };

  private getErrorMessage(): { title: string; description: string } {
    switch (this.state.errorType) {
      case 'camera':
        return {
          title: 'Camera Access Required',
          description: 'Please allow camera access to scan book pages. Check your browser settings if the permission was blocked.',
        };
      case 'scanner':
        return {
          title: 'Scanner Initialization Failed',
          description: 'The document scanner could not start. This might be a temporary issue. Please try again.',
        };
      default:
        return {
          title: 'Something Went Wrong',
          description: 'An unexpected error occurred while scanning. Please try again or close and restart.',
        };
    }
  }

  public render() {
    if (this.state.hasError) {
      const { title, description } = this.getErrorMessage();
      const isDev = import.meta.env.DEV;

      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-6">
          <div 
            className="max-w-sm w-full p-6 rounded-3xl text-center"
            style={glassStyles.card}
          >
            {/* Icon */}
            <div 
              className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6"
              style={{ 
                background: this.state.errorType === 'camera' 
                  ? `linear-gradient(135deg, ${colors.status.warning}20, ${colors.status.warning}10)`
                  : `linear-gradient(135deg, ${colors.status.error}20, ${colors.status.error}10)`
              }}
            >
              {this.state.errorType === 'camera' ? (
                <Camera className="w-10 h-10 text-amber-500" />
              ) : (
                <AlertTriangle className="w-10 h-10 text-red-500" />
              )}
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              {title}
            </h2>
            
            {/* Description */}
            <p className="text-gray-600 mb-6 text-sm">
              {description}
            </p>
            
            {/* Dev-only error details */}
            {isDev && this.state.error && (
              <details className="text-left bg-gray-50 p-3 rounded-xl mb-6 text-xs">
                <summary className="font-medium text-gray-700 cursor-pointer">
                  Error Details
                </summary>
                <pre className="mt-2 text-gray-600 whitespace-pre-wrap break-all overflow-auto max-h-32">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
            
            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={this.handleClose}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
                Close
              </button>
              <button
                onClick={this.handleRetry}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-white rounded-xl font-medium transition-colors"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.brand.primary}, ${colors.brand.secondary})`,
                }}
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ScannerErrorBoundary;
